import { memo, MouseEvent, useContext } from 'react'
import { ListItemButton, styled } from '@mui/material'
import { RemovePersonIcon } from '$common/icons/remove-person'
import { WithQueryTitleBase } from '$common/room-list-search/with-query-title-base'
import { getUserFirstName } from '$logic/users'
import { stringToColor } from '$func/string-to-color'
import { AvatarBase } from '$common/avatar/avatar-base'
import { getAvatarText } from '$func/avatar-text'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { useRoom } from '$logic/room'
import { ForwardMenuSearchQueryContext } from '$comp/modals/forward-menu/forward-menu'
import { RecentUserItemSkeleton } from '$common/recent-user-item/recent-user-item-skeleton'
import { getRoomImageAndName } from '$func/room-image-and-name'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { BadgeWrapper } from '../badge/badge'

const Root = styled(ListItemButton)`
  min-width: 82px;
  text-align: center;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0 5px;
  box-sizing: border-box;
  overflow: hidden;
  gap: 8px;
`

interface Props {
  roomId: string
  selected: boolean
  onClick: (chatId: string) => void
  onContextMenu: (chatId: string) => (e?: MouseEvent) => void
}

const StyledAvatar = styled(AvatarBase)`
  transition: all 0.2s;
`

export const ForwardMenuItem = memo(function ForwardMenuItemComponent({
  roomId,
  selected,
  onClick,
  onContextMenu,
}: Props) {
  const { type: roomType } = useRoom(roomId) || {}
  const query = useContext(ForwardMenuSearchQueryContext)

  const { value, loading } = useAsyncFunction(
    () => getRoomImageAndName(roomType, roomId),
    [roomType, roomId],
  )

  if (loading || !value) return <RecentUserItemSkeleton />

  const { image, name, user, hall } = value || {}

  const backgroundColor =
    roomType === 'PV'
      ? stringToColor(getUserFirstName(user), user?.id)
      : roomType === 'CHANNEL' || roomType === 'GROUP'
      ? stringToColor(hall?.name, roomId)
      : ''

  return (
    <Root
      onClick={() => onClick(roomId)}
      onContextMenu={e => onContextMenu(roomId)(e)}
    >
      <BadgeWrapper selected={selected}>
        <StyledAvatar
          size={selected ? AvatarSize.MD : AvatarSize.LG}
          backgroundColor={backgroundColor}
          src={image}
          hasDeleted={user?.deletedAt}
        >
          {user?.deletedAt ? <RemovePersonIcon /> : getAvatarText(name)}
        </StyledAvatar>
      </BadgeWrapper>

      <WithQueryTitleBase
        maxLines={2}
        query={query}
        title={name}
        variant="caption1"
      />
    </Root>
  )
})
